import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';

interface Project {
  id: string;
  kodProjek: string;
  namaProjek: string;
  pelanggan: string;
  nilaiProjek: number;
  kosProjek: number;
  status: 'Dalam Proses' | 'Selesai' | 'Tender' | 'Dibatalkan';
  tarikhMula: string;
  tarikhSiap: string;
  catatan?: string;
}

interface ProjectFormProps {
  project?: Project;
  kodProjek: string;
  customers?: any[];
  onSubmit: (data: Project) => void;
  onCancel: () => void;
}

export const ProjectForm = ({ project, kodProjek, customers = [], onSubmit, onCancel }: ProjectFormProps) => {
  const [formData, setFormData] = useState<Partial<Project>>(
    project || {
      id: Date.now().toString(),
      kodProjek: kodProjek,
      namaProjek: '',
      pelanggan: '',
      nilaiProjek: 0,
      kosProjek: 0,
      status: 'Dalam Proses',
      tarikhMula: new Date().toISOString().split('T')[0],
      tarikhSiap: '',
      catatan: '',
    }
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchPelanggan, setSearchPelanggan] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: name === 'nilaiProjek' || name === 'kosProjek' ? parseFloat(value) || 0 : value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectPelanggan = (namaSyarikat: string) => {
    setFormData(prev => ({ ...prev, pelanggan: namaSyarikat }));
    setSearchPelanggan('');
    if (errors.pelanggan) {
      setErrors(prev => ({ ...prev, pelanggan: '' }));
    }
  };

  const filteredPelanggan = customers.filter(c =>
    c.namaSyarikat.toLowerCase().includes(searchPelanggan.toLowerCase())
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    if (!formData.namaProjek?.trim()) newErrors.namaProjek = 'Nama projek diperlukan';
    if (!formData.pelanggan?.trim()) newErrors.pelanggan = 'Nama pelanggan diperlukan';
    if (!formData.nilaiProjek || formData.nilaiProjek <= 0) newErrors.nilaiProjek = 'Nilai projek harus lebih dari 0';
    if (!formData.kosProjek || formData.kosProjek < 0) newErrors.kosProjek = 'Kos projek tidak boleh negatif';
    if (!formData.tarikhMula?.trim()) newErrors.tarikhMula = 'Tarikh mula diperlukan';
    if (!formData.tarikhSiap?.trim()) newErrors.tarikhSiap = 'Tarikh siap diperlukan';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData as Project);
  };

  const keuntungan = (formData.nilaiProjek || 0) - (formData.kosProjek || 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex justify-between items-center sticky top-0 bg-white border-b">
          <CardTitle>Projek Baru</CardTitle>
          <button onClick={onCancel} className="p-2 hover:bg-slate-100 rounded">
            <X size={20} />
          </button>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Nama Projek */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Nama Projek *</label>
              <Input
                type="text"
                name="namaProjek"
                value={formData.namaProjek || ''}
                onChange={handleChange}
                placeholder="Contoh: Pembinaan Kompleks Perniagaan"
                className={errors.namaProjek ? 'border-red-500' : ''}
              />
              {errors.namaProjek && <p className="text-sm text-red-600">{errors.namaProjek}</p>}
            </div>

            {/* Kod Projek (Auto) */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Kod Projek</label>
              <Input
                type="text"
                value={formData.kodProjek || ''}
                disabled
                className="bg-slate-100 text-slate-500"
              />
              <p className="text-xs text-slate-500">Kod akan di-jana secara automatik</p>
            </div>

            {/* Nama Pelanggan - Dropdown Carian */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Nama Pelanggan *</label>
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Cari atau pilih pelanggan..."
                  value={searchPelanggan || (formData as any).pelanggan || ''}
                  onChange={(e) => setSearchPelanggan(e.target.value)}
                  className={errors.pelanggan ? 'border-red-500' : ''}
                />
                {(searchPelanggan || !(formData as any).pelanggan) && searchPelanggan !== (formData as any).pelanggan && (
                  <div className="absolute top-full left-0 right-0 border border-slate-300 border-t-0 rounded-b-lg bg-white shadow-lg z-10 max-h-48 overflow-y-auto">
                    {filteredPelanggan.length > 0 ? (
                      filteredPelanggan.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => handleSelectPelanggan(c.namaSyarikat)}
                          className="w-full text-left px-4 py-2 hover:bg-slate-100 border-b border-slate-200 last:border-b-0"
                        >
                          <div className="font-medium text-slate-900">{c.namaSyarikat}</div>
                          <div className="text-xs text-slate-500">{c.email} • {c.telp}</div>
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-sm text-slate-500 text-center">
                        Tiada pelanggan ditemui
                      </div>
                    )}
                  </div>
                )}
                {(formData as any).pelanggan && !searchPelanggan && (
                  <div className="absolute top-full left-0 right-0 border border-slate-300 border-t-0 rounded-b-lg bg-white shadow-lg z-10 p-2">
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, pelanggan: '' }));
                        setSearchPelanggan('');
                      }}
                      className="w-full text-left text-sm text-red-600 hover:text-red-700 py-1"
                    >
                      Bersihkan Pilihan
                    </button>
                  </div>
                )}
              </div>
              {errors.pelanggan && <p className="text-sm text-red-600">{errors.pelanggan}</p>}
            </div>

            {/* Nilai Projek & Kos Projek */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Nilai Projek (RM) *</label>
                <Input
                  type="number"
                  name="nilaiProjek"
                  value={formData.nilaiProjek || ''}
                  onChange={handleChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className={errors.nilaiProjek ? 'border-red-500' : ''}
                />
                {errors.nilaiProjek && <p className="text-sm text-red-600">{errors.nilaiProjek}</p>}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Kos Projek (RM) *</label>
                <Input
                  type="number"
                  name="kosProjek"
                  value={formData.kosProjek || ''}
                  onChange={handleChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className={errors.kosProjek ? 'border-red-500' : ''}
                />
                {errors.kosProjek && <p className="text-sm text-red-600">{errors.kosProjek}</p>}
              </div>
            </div>

            {/* Keuntungan (Auto-calculated) */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-600">Keuntungan Kasar</p>
              <p className="text-2xl font-bold text-blue-600">RM {keuntungan.toFixed(2)}</p>
            </div>

            {/* Status */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Status</label>
              <select
                name="status"
                value={formData.status || 'Dalam Proses'}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="Dalam Proses">Dalam Proses</option>
                <option value="Selesai">Selesai</option>
                <option value="Tender">Tender</option>
                <option value="Dibatalkan">Dibatalkan</option>
              </select>
            </div>

            {/* Tarikh Mula & Tarikh Siap */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Tarikh Mula *</label>
                <Input
                  type="date"
                  name="tarikhMula"
                  value={formData.tarikhMula || ''}
                  onChange={handleChange}
                  className={errors.tarikhMula ? 'border-red-500' : ''}
                />
                {errors.tarikhMula && <p className="text-sm text-red-600">{errors.tarikhMula}</p>}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Tarikh Siap *</label>
                <Input
                  type="date"
                  name="tarikhSiap"
                  value={formData.tarikhSiap || ''}
                  onChange={handleChange}
                  className={errors.tarikhSiap ? 'border-red-500' : ''}
                />
                {errors.tarikhSiap && <p className="text-sm text-red-600">{errors.tarikhSiap}</p>}
              </div>
            </div>

            {/* Catatan */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Catatan</label>
              <textarea
                name="catatan"
                value={formData.catatan || ''}
                onChange={handleChange}
                placeholder="Catatan projek (pilihan)"
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-6 border-t">
              <Button type="submit" className="bg-red-600 hover:bg-red-700 flex-1">
                Simpan
              </Button>
              <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
                Batal
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
